import { type TResolution } from '@streaming/doppio-player';
import { EPlaybackState, type EPlayerType } from '@streaming/mouflon-utils';

import { IStreamConfig, TPlayerError } from '@customTypes/common';

export type TStreamLoadErrorReason = 'HTTP_ERROR' | 'WEBSOCKET_ERROR' | 'SIGNALLING_STATUS_ERROR';

export enum EPlayersEvents {
  MODULE_LOADED = 'playerEvents.moduleLoaded',
  PLAYBACK_STATE_CHANGED = 'playerEvents.playbackStateChanged',
  DEBUGGER_CONNECTED = 'playerEvents.debuggerConnected',
  ERROR = 'playerEvents.error',
  AUTO_RESOLUTION_SWITCHED = 'playerEvents.autoResolutionSwitched',
  RESOLUTION_SWITCHED = 'playerEvents.resolutionSwitched',
  VIDEO_PLAYBACK_STATE_CHANGED = 'playerEvents.videoPlaybackStateChanged',
  RESOLUTIONS_AVAILABLE = 'playerEvents.resolutionsAvailable',
  RESOLUTION_NOT_FOUND = 'playerEvents.resolutionNotFound',
  VIDEO_ELEMENT_UPDATED = 'playerEvents.videoElementUpdated',
  PLAYER_TYPES_AVAILABLE = 'playerEvents.playerTypesAvailable',
  TOGGLE_TURN_SERVERS = 'playerEvents.toggleTURNServers',
  FORCE_PLAYER_TYPE = 'playerEvents.forcePlayerType',
  PLAYER_TYPE_CHANGED = 'playerEvents.playerTypeChanged',
  STREAM_CONFIG_UPDATED = 'playerEvents.streamConfigUpdated',
  STREAM_CONFIG_UPDATED_INTERNAL = 'playerEvents.streamConfigUpdatedInternal',
  BROADCAST_SUPPORTED_CHANGE = 'playerEvents.broadcastSupportedChange',
  LATENCY_ESTIMATION_AVAILABLE = 'playerEvents.latencyEstimationAvailable',
  STREAM_LOAD_ERROR = 'playerEvents.streamLoadError',
}

export interface IPlayersEventsMap {
  [EPlayersEvents.MODULE_LOADED]: never;
  [EPlayersEvents.PLAYBACK_STATE_CHANGED]: { prevState: EPlaybackState; nextState: EPlaybackState };
  [EPlayersEvents.DEBUGGER_CONNECTED]: { debuggerUrl: string };
  [EPlayersEvents.ERROR]: TPlayerError;
  [EPlayersEvents.VIDEO_PLAYBACK_STATE_CHANGED]: {
    prevState: EPlaybackState;
    nextState: EPlaybackState;
  };
  [EPlayersEvents.RESOLUTIONS_AVAILABLE]: {
    resolutions: TResolution[];
  };
  [EPlayersEvents.AUTO_RESOLUTION_SWITCHED]: {
    name: string;
  };

  [EPlayersEvents.RESOLUTION_SWITCHED]: {
    name: string;
  };

  [EPlayersEvents.RESOLUTION_NOT_FOUND]: unknown;
  [EPlayersEvents.VIDEO_ELEMENT_UPDATED]: {
    videoElement: HTMLVideoElement | null;
  };
  [EPlayersEvents.PLAYER_TYPES_AVAILABLE]: {
    availablePlayerTypes: EPlayerType[];
  };
  [EPlayersEvents.TOGGLE_TURN_SERVERS]: {
    shouldUseTURNServers: boolean;
  };
  [EPlayersEvents.FORCE_PLAYER_TYPE]: {
    playerType: EPlayerType;
  };
  [EPlayersEvents.PLAYER_TYPE_CHANGED]: {
    playerType: EPlayerType;
  };
  [EPlayersEvents.STREAM_CONFIG_UPDATED]: {
    streamConfig: IStreamConfig;
  };
  [EPlayersEvents.STREAM_CONFIG_UPDATED_INTERNAL]: {
    streamConfig: IStreamConfig;
  };
  [EPlayersEvents.BROADCAST_SUPPORTED_CHANGE]: {
    isSupported: boolean;
  };
  [EPlayersEvents.LATENCY_ESTIMATION_AVAILABLE]: {
    medianLatency: number;
    EWMALatency: number;
  };
  [EPlayersEvents.STREAM_LOAD_ERROR]: {
    reason: TStreamLoadErrorReason;
    httpStatusCode?: number;
    wsStatusCode?: number;
    signallingMessage?: string;
  };
}

export type TPlayersEventsPayload<T extends keyof IPlayersEventsMap> = IPlayersEventsMap[T];
